import React, { useState, useEffect } from "react";
import { useLocation, useHistory } from "react-router-dom";
import {
  Receipt,
  Users,
  Menu,
  X,
  ChevronLeft,
  ArrowLeft,
  Briefcase,
  BarChart3,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAppContext } from "../../../AppContext";
import AccountantContractorEmployees from "./AccountantContractorEmployees";
import AccountantContractorExpenses from "./AccountantContractorExpenses";
import axios from "axios";
import { MyDomain } from "../../../App";

const COLORS = {
  primary: "#0ea5e9",
  secondary: "#0284c7",
  background: "#ffffff",
  inactive: "#94a3b8",
  border: "#e2e8f0",
};

export default function AccountantHomeContractorMainPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const history = useHistory();
  const { contractorId, contractorName, contractorData } = location.state || {};
  const { Lang, AllData, setAllData } = useAppContext();
  const isRTL = Lang === "ar" || Lang === "he";

  const [activeTab, setActiveTab] = useState("expenses");
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    fetchContractorData();
  }, [contractorId]);

  const fetchContractorData = async () => {
    try {
      setLoading(true);
      const response = await axios.post(`${MyDomain}Employee/GetAllData`, {
        UserId: contractorId,
      });

      console.log("✅ Contractor data loaded:", response.data);
      setAllData(response.data);
    } catch (error) {
      console.error("❌ Error fetching contractor data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleGoBack = () => {
    history.goBack();
  };

  if (loading) {
    return (
      <>
        <style>
          {`
            @keyframes spin {
              to { transform: rotate(360deg); }
            }
          `}
        </style>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "100vh",
            backgroundColor: "#F9FAFB",
          }}
        >
          <div
            style={{
              width: "48px",
              height: "48px",
              border: "4px solid #e5e8eb",
              borderTopColor: COLORS.primary,
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
            }}
          />
          <p
            style={{
              marginTop: "16px",
              fontSize: "16px",
              color: "#6B7280",
              textAlign: "center",
            }}
          >
            {t("loading")}
          </p>
        </div>
      </>
    );
  }

  const menuItems = [
    {
      id: "expenses",
      label: t("expenses"),
      icon: Receipt,
      color: "#0ea5e9",
      component: AccountantContractorExpenses,
    },
    {
      id: "employees",
      label: t("employees"),
      icon: Users,
      color: "#8b5cf6",
      component: AccountantContractorEmployees,
    },
  ];

  const ActiveComponent = menuItems.find(
    (item) => item.id === activeTab
  )?.component;

  return (
    <div
      style={{
        display: "flex",
        height: "100vh",
        backgroundColor: "#F9FAFB",
        direction: isRTL ? "rtl" : "ltr",
        overflow: "hidden",
      }}
    >
      <style>{`
        @keyframes slideIn {
          from { transform: translateX(${isRTL ? "100%" : "-100%"}); }
          to { transform: translateX(0); }
        }
        .animate-slide-in {
          animation: slideIn 0.3s ease-out;
        }
        
        /* Scrollbar Styling */
        ::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        ::-webkit-scrollbar-track {
          background: #f1f1f1;
        }
        ::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 4px;
        }
        ::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
        
        /* Tooltip hover effect */
        button:hover .tooltip {
          opacity: 1 !important;
          visibility: visible !important;
        }
      `}</style>

      {/* Sidebar - Desktop */}
      <aside
        style={{
          display: sidebarOpen ? "flex" : "none",
          flexDirection: "column",
          backgroundColor: COLORS.background,
          borderRight: isRTL ? "none" : `1px solid ${COLORS.border}`,
          borderLeft: isRTL ? `1px solid ${COLORS.border}` : "none",
          width: sidebarOpen ? "280px" : "80px",
          transition: "all 0.3s ease",
          position: "relative",
        }}
        className="hidden lg:flex"
      >
        {/* Logo/Header Section */}
        <div
          style={{
            height: "72px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0 20px",
            borderBottom: `1px solid ${COLORS.border}`,
            backgroundColor: "#FFFFFF",
            flexDirection: isRTL ? "row-reverse" : "row",
          }}
        >
          {sidebarOpen ? (
            <>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  flexDirection: isRTL ? "row-reverse" : "row",
                }}
              >
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "10px",
                    backgroundColor: "#0ea5e915",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Briefcase size={22} color="#0ea5e9" />
                </div>
                <span
                  style={{
                    fontSize: "18px",
                    fontWeight: 700,
                    color: "#111827",
                  }}
                >
                  {t("contractor")}
                </span>
              </div>
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                style={{
                  padding: "8px",
                  borderRadius: "8px",
                  border: "none",
                  backgroundColor: "transparent",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "background-color 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#F3F4F6";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                }}
              >
                <ChevronLeft
                  size={20}
                  color="#6B7280"
                  style={{
                    transform: isRTL
                      ? sidebarOpen
                        ? "rotate(0deg)"
                        : "rotate(180deg)"
                      : sidebarOpen
                      ? "rotate(180deg)"
                      : "rotate(0deg)",
                  }}
                />
              </button>
            </>
          ) : (
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                padding: "8px",
                borderRadius: "8px",
                border: "none",
                backgroundColor: "transparent",
                cursor: "pointer",
              }}
            >
              <Briefcase size={24} color="#0ea5e9" />
            </button>
          )}
        </div>

        {/* Contractor Info */}
        {sidebarOpen && (
          <div
            style={{
              padding: "20px",
              borderBottom: `1px solid ${COLORS.border}`,
              backgroundColor: "#FFFFFF",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                flexDirection: isRTL ? "row-reverse" : "row",
              }}
            >
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  borderRadius: "12px",
                  backgroundColor: "#10B98115",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexShrink: 0,
                }}
              >
                <span
                  style={{
                    fontSize: "20px",
                    fontWeight: 700,
                    color: "#10B981",
                  }}
                >
                  {contractorName?.charAt(0)?.toUpperCase() || "C"}
                </span>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p
                  style={{
                    fontSize: "15px",
                    fontWeight: 600,
                    color: "#111827",
                    margin: "0 0 2px 0",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    textAlign: isRTL ? "right" : "left",
                  }}
                >
                  {contractorName || t("contractor")}
                </p>
                <p
                  style={{
                    fontSize: "12px",
                    color: "#6B7280",
                    margin: 0,
                    textAlign: isRTL ? "right" : "left",
                  }}
                >
                  {t("contractorAccount")}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Collapsed Contractor Avatar */}
        {!sidebarOpen && (
          <div
            style={{
              padding: "20px",
              borderBottom: `1px solid ${COLORS.border}`,
              display: "flex",
              justifyContent: "center",
              backgroundColor: "#FFFFFF",
            }}
          >
            <div
              style={{
                width: "48px",
                height: "48px",
                borderRadius: "12px",
                backgroundColor: "#10B98115",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <span
                style={{
                  fontSize: "20px",
                  fontWeight: 700,
                  color: "#10B981",
                }}
              >
                {contractorName?.charAt(0)?.toUpperCase() || "C"}
              </span>
            </div>
          </div>
        )}

        {/* Navigation Menu */}
        <nav
          style={{
            flex: 1,
            padding: "16px",
            overflowY: "auto",
            backgroundColor: "#FFFFFF",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeTab === item.id;

              return (
                <div key={item.id} style={{ position: "relative" }}>
                  <button
                    onClick={() => setActiveTab(item.id)}
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      gap: sidebarOpen ? "12px" : "0",
                      justifyContent: sidebarOpen ? "flex-start" : "center",
                      padding: "14px 16px",
                      borderRadius: "10px",
                      border: "none",
                      backgroundColor: isActive ? "#EFF6FF" : "transparent",
                      color: isActive ? COLORS.primary : "#6B7280",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      boxShadow: isActive
                        ? "0 1px 3px rgba(0, 0, 0, 0.05)"
                        : "none",
                      flexDirection: isRTL ? "row-reverse" : "row",
                    }}
                    onMouseEnter={(e) => {
                      if (!isActive) {
                        e.currentTarget.style.backgroundColor = "#F9FAFB";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive) {
                        e.currentTarget.style.backgroundColor = "transparent";
                      }
                    }}
                  >
                    <Icon
                      size={22}
                      color={isActive ? item.color : "#6B7280"}
                      style={{ flexShrink: 0 }}
                    />
                    {sidebarOpen && (
                      <span
                        style={{
                          fontSize: "14px",
                          fontWeight: isActive ? 600 : 500,
                          color: isActive ? item.color : "#374151",
                        }}
                      >
                        {item.label}
                      </span>
                    )}

                    {/* Tooltip for collapsed sidebar */}
                    {!sidebarOpen && (
                      <div
                        className="tooltip"
                        style={{
                          position: "absolute",
                          left: isRTL ? "auto" : "100%",
                          right: isRTL ? "100%" : "auto",
                          marginLeft: isRTL ? 0 : "8px",
                          marginRight: isRTL ? "8px" : 0,
                          top: "50%",
                          transform: "translateY(-50%)",
                          padding: "8px 12px",
                          backgroundColor: "#1F2937",
                          color: "#FFFFFF",
                          fontSize: "13px",
                          borderRadius: "8px",
                          opacity: 0,
                          visibility: "hidden",
                          transition: "all 0.2s",
                          whiteSpace: "nowrap",
                          zIndex: 50,
                          pointerEvents: "none",
                        }}
                      >
                        {item.label}
                        <div
                          style={{
                            position: "absolute",
                            top: "50%",
                            transform: "translateY(-50%)",
                            right: isRTL ? "auto" : "100%",
                            left: isRTL ? "100%" : "auto",
                            borderWidth: "4px",
                            borderStyle: "solid",
                            borderColor: "transparent",
                            borderRightColor: isRTL ? "transparent" : "#1F2937",
                            borderLeftColor: isRTL ? "#1F2937" : "transparent",
                          }}
                        />
                      </div>
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        </nav>

        {/* Back Button */}
        <div
          style={{
            padding: "16px",
            borderTop: `1px solid ${COLORS.border}`,
            backgroundColor: "#FFFFFF",
          }}
        >
          <button
            onClick={handleGoBack}
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              gap: sidebarOpen ? "12px" : "0",
              justifyContent: sidebarOpen ? "flex-start" : "center",
              padding: "14px 16px",
              borderRadius: "10px",
              border: "none",
              backgroundColor: "transparent",
              color: "#EF4444",
              cursor: "pointer",
              transition: "background-color 0.2s",
              flexDirection: isRTL ? "row-reverse" : "row",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#FEF2F2";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
            }}
          >
            <ArrowLeft
              size={22}
              color="#EF4444"
              style={{
                flexShrink: 0,
                transform: isRTL ? "rotate(180deg)" : "none",
              }}
            />
            {sidebarOpen && (
              <span style={{ fontSize: "14px", fontWeight: 600 }}>
                {t("back")}
              </span>
            )}
          </button>
        </div>
      </aside>

      {/* Mobile Sidebar Overlay */}
      {mobileMenuOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 40,
          }}
          className="lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <aside
        style={{
          position: "fixed",
          top: 0,
          bottom: 0,
          left: isRTL ? "auto" : 0,
          right: isRTL ? 0 : "auto",
          width: "280px",
          backgroundColor: COLORS.background,
          borderRight: isRTL ? "none" : `1px solid ${COLORS.border}`,
          borderLeft: isRTL ? `1px solid ${COLORS.border}` : "none",
          zIndex: 50,
          transform: mobileMenuOpen
            ? "translateX(0)"
            : `translateX(${isRTL ? "100%" : "-100%"})`,
          transition: "transform 0.3s ease",
          display: "flex",
          flexDirection: "column",
        }}
        className="lg:hidden"
      >
        {/* Logo/Header Section */}
        <div
          style={{
            height: "72px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0 20px",
            borderBottom: `1px solid ${COLORS.border}`,
            flexDirection: isRTL ? "row-reverse" : "row",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              flexDirection: isRTL ? "row-reverse" : "row",
            }}
          >
            <div
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "10px",
                backgroundColor: "#0ea5e915",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Briefcase size={22} color="#0ea5e9" />
            </div>
            <span
              style={{
                fontSize: "18px",
                fontWeight: 700,
                color: "#111827",
              }}
            >
              {t("contractor")}
            </span>
          </div>
          <button
            onClick={() => setMobileMenuOpen(false)}
            style={{
              padding: "8px",
              borderRadius: "8px",
              border: "none",
              backgroundColor: "transparent",
              cursor: "pointer",
            }}
          >
            <X size={20} color="#6B7280" />
          </button>
        </div>

        {/* Contractor Info */}
        <div
          style={{
            padding: "20px",
            borderBottom: `1px solid ${COLORS.border}`,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              flexDirection: isRTL ? "row-reverse" : "row",
            }}
          >
            <div
              style={{
                width: "48px",
                height: "48px",
                borderRadius: "12px",
                backgroundColor: "#10B98115",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexShrink: 0,
              }}
            >
              <span
                style={{
                  fontSize: "20px",
                  fontWeight: 700,
                  color: "#10B981",
                }}
              >
                {contractorName?.charAt(0)?.toUpperCase() || "C"}
              </span>
            </div>
            <div style={{ flex: 1 }}>
              <p
                style={{
                  fontSize: "15px",
                  fontWeight: 600,
                  color: "#111827",
                  margin: "0 0 2px 0",
                  textAlign: isRTL ? "right" : "left",
                }}
              >
                {contractorName || t("contractor")}
              </p>
              <p
                style={{
                  fontSize: "12px",
                  color: "#6B7280",
                  margin: 0,
                  textAlign: isRTL ? "right" : "left",
                }}
              >
                {t("contractorAccount")}
              </p>
            </div>
          </div>
        </div>

        {/* Navigation Menu */}
        <nav
          style={{
            flex: 1,
            padding: "16px",
            overflowY: "auto",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeTab === item.id;

              return (
                <button
                  key={item.id}
                  onClick={() => {
                    setActiveTab(item.id);
                    setMobileMenuOpen(false);
                  }}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "14px 16px",
                    borderRadius: "10px",
                    border: "none",
                    backgroundColor: isActive ? "#EFF6FF" : "transparent",
                    color: isActive ? COLORS.primary : "#6B7280",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    flexDirection: isRTL ? "row-reverse" : "row",
                  }}
                >
                  <Icon
                    size={22}
                    color={isActive ? item.color : "#6B7280"}
                    style={{ flexShrink: 0 }}
                  />
                  <span
                    style={{
                      fontSize: "14px",
                      fontWeight: isActive ? 600 : 500,
                      color: isActive ? item.color : "#374151",
                    }}
                  >
                    {item.label}
                  </span>
                </button>
              );
            })}
          </div>
        </nav>

        {/* Back Button */}
        <div
          style={{
            padding: "16px",
            borderTop: `1px solid ${COLORS.border}`,
          }}
        >
          <button
            onClick={handleGoBack}
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              gap: "12px",
              padding: "14px 16px",
              borderRadius: "10px",
              border: "none",
              backgroundColor: "transparent",
              color: "#EF4444",
              cursor: "pointer",
              transition: "background-color 0.2s",
              flexDirection: isRTL ? "row-reverse" : "row",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#FEF2F2";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
            }}
          >
            <ArrowLeft
              size={22}
              color="#EF4444"
              style={{
                flexShrink: 0,
                transform: isRTL ? "rotate(180deg)" : "none",
              }}
            />
            <span style={{ fontSize: "14px", fontWeight: 600 }}>
              {t("back")}
            </span>
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Top Header */}
        <header
          style={{
            height: "72px",
            backgroundColor: COLORS.background,
            borderBottom: `1px solid ${COLORS.border}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0 24px",
            flexDirection: isRTL ? "row-reverse" : "row",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "16px",
              flexDirection: isRTL ? "row-reverse" : "row",
            }}
          >
            <button
              onClick={() => setMobileMenuOpen(true)}
              style={{
                padding: "8px",
                borderRadius: "8px",
                border: "none",
                backgroundColor: "transparent",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              className="lg:hidden"
            >
              <Menu size={24} color="#6B7280" />
            </button>
            <h1
              style={{
                fontSize: "20px",
                fontWeight: 700,
                color: "#111827",
                margin: 0,
                textAlign: isRTL ? "right" : "left",
              }}
            >
              {menuItems.find((item) => item.id === activeTab)?.label ||
                t("dashboard")}
            </h1>
          </div>

          {/* Header Actions */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              flexDirection: isRTL ? "row-reverse" : "row",
            }}
          >
            {/* Notification Bell */}
            <button
              style={{
                position: "relative",
                padding: "8px",
                borderRadius: "8px",
                border: "none",
                backgroundColor: "transparent",
                cursor: "pointer",
                transition: "background-color 0.2s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#F3F4F6";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              <svg
                width="24"
                height="24"
                fill="none"
                stroke="#6B7280"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                />
              </svg>
              <span
                style={{
                  position: "absolute",
                  top: "6px",
                  right: isRTL ? "auto" : "6px",
                  left: isRTL ? "6px" : "auto",
                  width: "8px",
                  height: "8px",
                  backgroundColor: "#EF4444",
                  borderRadius: "50%",
                }}
              />
            </button>

            {/* Stats Icon */}
            <button
              style={{
                padding: "8px",
                borderRadius: "8px",
                border: "none",
                backgroundColor: "transparent",
                cursor: "pointer",
                transition: "background-color 0.2s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#F3F4F6";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              <BarChart3 size={24} color="#6B7280" />
            </button>

            {/* User Avatar - Mobile */}
            <div
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "10px",
                backgroundColor: "#10B98115",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
              className="lg:hidden"
            >
              <span
                style={{
                  fontSize: "16px",
                  fontWeight: 700,
                  color: "#10B981",
                }}
              >
                {contractorName?.charAt(0)?.toUpperCase() || "C"}
              </span>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main
          style={{
            flex: 1,
            overflowY: "auto",
            backgroundColor: "#F9FAFB",
          }}
        >
          {ActiveComponent && (
            <ActiveComponent
              contractorId={contractorId}
              contractorName={contractorName}
              contractorData={contractorData}
            />
          )}
        </main>
      </div>
    </div>
  );
}
